import * as React from 'react';

import type { SidecarOperatingSystem } from '@rematter/frontend-lib-api-client';
import { Badge, Button, ContentSection, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { ArrowLeft, CheckCircle, Cpu } from 'lucide-react';

import { sidecarRegisterAtom } from '../../../_graphql/mutationSidecarRegister';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { $remoteSidecars } from '../../../_state/sidecarsList.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

export const SidecarRegisterView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const toast = useToast();
  const registerSidecar = useSetAtom(
    sidecarRegisterAtom({
      onSuccess: (get) => {
        get($remoteSidecars).refetch?.();
      },
    }),
  );

  const params = React.useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      sidecarId: searchParams.get('id') ?? '',
      publicKey: searchParams.get('publicKey') ?? '',
      name: searchParams.get('name') ?? '',
      os: searchParams.get('os') ?? '',
      osVersion: searchParams.get('osVersion') ?? '',
    };
  }, []);

  const [facilityId, setFacilityId] = React.useState('');
  const [name, setName] = React.useState(params.name);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);

  const isMissingParams = !params.sidecarId || !params.publicKey;

  const handleBack = React.useCallback(() => {
    setLocation('/hardware');
  }, [setLocation]);

  const handleFacilityChange = React.useCallback(
    (value: string | undefined) => {
      setFacilityId(value ?? '');
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!facilityId || !params.sidecarId || !params.publicKey) return;

    try {
      setIsSubmitting(true);
      await registerSidecar({
        sidecarId: params.sidecarId,
        publicKey: params.publicKey,
        facilityId,
        name: name || null,
        operatingSystem: (params.os as SidecarOperatingSystem) || null,
        operatingSystemVersion: params.osVersion || null,
      });
      setIsSuccess(true);
      toast.success(t.hardwareView.sidecarRegisterView.messages.success);
    } catch (err) {
      toast.error(
        t.hardwareView.sidecarRegisterView.messages.error(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [facilityId, params, name, registerSidecar, toast, t]);

  if (isMissingParams) {
    return (
      <div className="@SidecarRegisterView flex h-full flex-col items-center justify-center gap-4 p-6">
        <Cpu className="size-12 text-neutral/40" />
        <p className="text-center text-sm text-neutral/60">
          {t.hardwareView.sidecarRegisterView.messages.missingParams}
        </p>
        <Button variant="outline" onPress={handleBack}>
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.sidecarRegisterView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="@SidecarRegisterView flex h-full flex-col items-center justify-center gap-4 p-6">
        <CheckCircle className="size-16 text-success" />
        <h2 className="text-xl font-semibold">
          {t.hardwareView.sidecarRegisterView.title}
        </h2>
        <p className="text-center text-sm text-neutral/60">
          {t.hardwareView.sidecarRegisterView.messages.success}
        </p>
        <Button variant="outline" onPress={handleBack}>
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.sidecarRegisterView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  return (
    <div className="@SidecarRegisterView flex h-full flex-col">
      <div className="border-b border-neutral/10 p-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="small"
            onPress={handleBack}
            aria-label={
              t.hardwareView.sidecarRegisterView.actions.backToHardware
            }
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h2 className="text-lg font-semibold">
              {t.hardwareView.sidecarRegisterView.title}
            </h2>
            <p className="text-sm text-neutral/60">
              {t.hardwareView.sidecarRegisterView.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col gap-6">
          <ContentSection
            title={
              t.hardwareView.sidecarRegisterView.sections.deviceInformation
            }
            variant="panel"
          >
            <div className="flex min-w-0 flex-col gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral/60">
                  {t.hardwareView.sidecarRegisterView.fields.sidecarId}
                </span>
                <div className="flex items-center gap-2">
                  <code className="rounded bg-neutral/5 px-2 py-1 font-mono text-sm">
                    {params.sidecarId}
                  </code>
                </div>
              </div>
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-sm text-neutral/60">
                  {t.hardwareView.sidecarRegisterView.fields.publicKey}
                </span>
                <code className="rounded bg-neutral/5 px-2 py-1 font-mono text-xs break-all">
                  {params.publicKey}
                </code>
              </div>
              {params.os && (
                <div className="flex items-center gap-2">
                  <Badge color="primary">{params.os}</Badge>
                  <Badge color="neutral">{params.osVersion}</Badge>
                </div>
              )}
            </div>
          </ContentSection>

          <ContentSection
            title={t.hardwareView.sidecarRegisterView.sections.configuration}
            variant="panel"
          >
            <div className="flex flex-col gap-4">
              <SelectFacility
                label={t.hardwareView.sidecarRegisterView.fields.facility}
                value={facilityId}
                onChange={handleFacilityChange}
                placeholder={
                  t.hardwareView.sidecarRegisterView.placeholders.facility
                }
                isRequired
              />
              <TextField
                label={t.hardwareView.sidecarRegisterView.fields.name}
                value={name}
                onChange={setName}
                placeholder={
                  t.hardwareView.sidecarRegisterView.placeholders.name
                }
              />
            </div>
          </ContentSection>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-neutral/10 p-4">
        <Button
          variant="solid"
          onPress={handleSubmit}
          isDisabled={!facilityId || isSubmitting}
        >
          {isSubmitting
            ? t.hardwareView.sidecarRegisterView.actions.registering
            : t.hardwareView.sidecarRegisterView.actions.register}
        </Button>
      </div>
    </div>
  );
};
